import React from 'react';
import Button from '@atlaskit/button';
import { Flex, Inline, Text, xcss } from '@atlaskit/primitives';
import { Checkbox } from '@atlaskit/checkbox';
import Tooltip from '@atlaskit/tooltip';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import PersonIcon from '@atlaskit/icon/core/person';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import DeleteIcon from '@atlaskit/icon/core/delete';
import './bulk-actions-section.scss';

const boxStyles = xcss({
  paddingLeft: 'space.050',
  marginTop: 'space.300',
});

type BulkActionsSectionProps = {
  numberOfItemsSelected: number;
  removeProductsFromCart: () => void;
  handleCheckedSelectAll: () => void;
  handleMatchBillDates: () => void;
  isIndeterminate: boolean;
  isChecked: boolean;
  isMatchBillDateBtnDisabled?: boolean;
  enableDesignIteration1Changes?: boolean;
  hideChangeUserButton?: boolean;
  handleBulkUserChange?: () => void;
};

export const BulkActionsSection: React.FC<BulkActionsSectionProps> = ({
  numberOfItemsSelected,
  removeProductsFromCart,
  handleCheckedSelectAll,
  handleMatchBillDates,
  isIndeterminate,
  isChecked,
  isMatchBillDateBtnDisabled = false,
  enableDesignIteration1Changes,
  hideChangeUserButton,
  handleBulkUserChange,
}) => {
  let matchBilldateDisablecontent = '';
  if (numberOfItemsSelected === 0) {
    matchBilldateDisablecontent = 'No subscription checked';
  } else if (isMatchBillDateBtnDisabled) {
    matchBilldateDisablecontent = 'For some checked new app, its parent product is not checked';
  }
  const matchBilldateDisableTooltip: React.ReactNode = <div className="pv-12">{matchBilldateDisablecontent}</div>;

  const bulkSelectionActions = (
    <Flex gap="space.150" alignItems="center" xcss={boxStyles}>
      <Inline space="space.150">
        <Checkbox
          isIndeterminate={isIndeterminate}
          isChecked={isChecked}
          testId="selected-item-checkbox"
          onChange={handleCheckedSelectAll}
          label=""
        />
        <Text color="color.text.subtle">{numberOfItemsSelected + ' ' + ' selected'}</Text>
      </Inline>

      {!hideChangeUserButton && (
        <Button
          testId="bulk-change-user-cta"
          onClick={handleBulkUserChange}
          iconBefore={<PersonIcon label="Change user" />}
        >
          Change quantity
        </Button>
      )}

      {isMatchBillDateBtnDisabled || numberOfItemsSelected === 0 ? (
        <Tooltip content={matchBilldateDisableTooltip} tag="span">
          <Button
            testId="bulk-change-bill-date-cta"
            onClick={handleMatchBillDates}
            iconBefore={<CalendarIcon label="Change bill date" />}
            isDisabled={true}
          >
            Change bill date
          </Button>
        </Tooltip>
      ) : (
        <Button
          testId="bulk-change-bill-date-cta"
          onClick={handleMatchBillDates}
          iconBefore={<CalendarIcon label="Change bill date" />}
        >
          Change bill date
        </Button>
      )}

      <Button
        testId="bulk-remove-cta"
        onClick={removeProductsFromCart}
        iconBefore={<DeleteIcon label="Remove from order" />}
      >
        Remove from order
      </Button>
    </Flex>
  );

  if (enableDesignIteration1Changes) {
    return bulkSelectionActions;
  }

  return (
    <div className="bulk-actions-container">
      <Checkbox
        isIndeterminate={isIndeterminate}
        isChecked={isChecked}
        testId="selected-item-checkbox"
        onChange={handleCheckedSelectAll}
        label=""
      />
      <label className="bulk-actions-container-label">
        {numberOfItemsSelected + ' ' + (numberOfItemsSelected <= 1 ? 'item' : 'items') + ' selected'}
      </label>
      {isMatchBillDateBtnDisabled || numberOfItemsSelected === 0 ? (
        <Tooltip content={matchBilldateDisableTooltip} tag="span">
          <Button testId="match-billdate-cta" appearance="default" onClick={handleMatchBillDates} isDisabled>
            Change bill date
          </Button>
        </Tooltip>
      ) : (
        <Button testId="match-billdate-cta" appearance="default" onClick={handleMatchBillDates}>
          Change bill date
        </Button>
      )}
      <Button
        testId="bulk-remove-cta"
        appearance="danger"
        className="remove-cta"
        onClick={removeProductsFromCart}
        isDisabled={numberOfItemsSelected === 0}
        iconBefore={<TrashIcon label="" size="small" />}
      >
        Remove
      </Button>
    </div>
  );
};
